/**
 * AdminUserGrid - AG Grid implementation for User & Access Management
 *
 * Displays users in a sortable, filterable grid with:
 * - ID, Email, Name, Role, Client, Supplier
 * - Action buttons (Edit, Generate Link, Delete)
 * - Role-based color coding
 */

import React, { useMemo } from "react";
import styled from "styled-components";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
  themeQuartz,
} from "ag-grid-community";
import type {
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
} from "ag-grid-community";
import { IconButton } from "@flexport/latitude-typescript";

// Register AG Grid v34 modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Configure AG Grid v34 theme
const gridTheme = themeQuartz;

/**
 * Grid wrapper with fixed height
 */
const GridWrapper = styled.div`
  width: 100%;
  height: 600px;
  margin-top: 20px;
`;

/**
 * Tooltip wrapper that properly shows tooltip when hovering over button and its children
 * The title attribute on the wrapper will trigger tooltips for the entire area
 */
const TooltipWrapper = styled.div`
  display: inline-flex;
  position: relative;
  cursor: pointer;

  /* Ensure button inherits the cursor and doesn't block tooltip */
  button {
    cursor: pointer;
  }
`;

/**
 * Actions container for button spacing with centered alignment
 */
const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

/**
 * Role tag with color coding
 */
const Tag = styled.span<{ color: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => props.color};
  color: white;
`;

/**
 * User row data interface
 */
export interface UserRow {
  id: string;
  email: string | null;
  name: string;
  role: string;
  createdAt: string;
  updatedAt: string;
  clientId: string | null;
  client?: {
    clientId: string;
    client_name: string;
    folder: string;
  } | null;
  supplierId: string | null;
  supplier?: {
    id: string;
    name: string;
    subfolder: string;
  } | null;
}

/**
 * Props for AdminUserGrid
 */
interface AdminUserGridProps {
  rows: ReadonlyArray<UserRow>;
  onEdit: (user: UserRow) => void;
  onGenerateLink: (userId: string) => void;
  onSendEmail: (userId: string) => void;
  onDelete: (id: string) => void;
  onViewLoginHistory: (user: UserRow) => void;
}

/**
 * Get role color for tag
 */
const getRoleColor = (role: string): string => {
  switch (role) {
    case "ADMIN":
      return "#f5222d";
    case "PORTWAY_EMPLOYEE":
      return "#1890ff";
    case "CUSTOMER":
      return "#52c41a";
    case "SUPPLIER":
      return "#fa8c16";
    default:
      return "#d9d9d9";
  }
};

/**
 * Get display text for role (shorten long role names)
 */
const getRoleDisplayText = (role: string): string => {
  if (role === "PORTWAY_EMPLOYEE") {
    return "PORTWAY";
  }
  return role;
};

/**
 * Role cell renderer with colored tag
 */
const RoleCellRenderer: React.FC<ICellRendererParams<UserRow>> = (params) => {
  const { data } = params;
  if (!data) return null;

  return (
    <Tag color={getRoleColor(data.role)}>{getRoleDisplayText(data.role)}</Tag>
  );
};

/**
 * Client value getter
 */
const clientGetter = (params: ValueGetterParams<UserRow>): string => {
  return params.data?.client?.client_name || "—";
};

/**
 * Supplier value getter
 */
const supplierGetter = (params: ValueGetterParams<UserRow>): string => {
  return params.data?.supplier?.name || "—";
};

/**
 * Actions cell renderer with Latitude IconButton components
 *
 * Note: ADMIN role users authenticate via Okta/CloudFlare and cannot use magic links,
 * so Link and Email actions are hidden for ADMIN users.
 */
const ActionsCellRenderer: React.FC<
  ICellRendererParams<UserRow> & {
    onEdit: (user: UserRow) => void;
    onGenerateLink: (userId: string) => void;
    onSendEmail: (userId: string) => void;
    onDelete: (id: string) => void;
    onViewLoginHistory: (user: UserRow) => void;
  }
> = (params) => {
  const { data } = params;
  const onEdit = (params as any).onEdit;
  const onGenerateLink = (params as any).onGenerateLink;
  const onSendEmail = (params as any).onSendEmail;
  const onDelete = (params as any).onDelete;
  const onViewLoginHistory = (params as any).onViewLoginHistory;

  if (!data) return null;

  // ADMIN users authenticate via Okta/CloudFlare, not magic links
  const isAdminUser = data.role === "ADMIN";

  return (
    <ActionsContainer>
      <TooltipWrapper title="Edit user details">
        <IconButton
          iconName="pencil"
          label="Edit"
          labelAriaOnly={true}
          iconAlignment="left"
          intent="basic"
          kind="hollow"
          size="s"
          type="button"
          isLoading={false}
          width="responsive"
          height={{ type: "fixed" }}
          disabled={false}
          temporaryUnsafeSetSolidKindHack={false}
          onClick={() => onEdit(data)}
          testId={`edit-user-${data.id}`}
        />
      </TooltipWrapper>
      {/* Hide Link and Email actions for ADMIN users - they use Okta/CloudFlare auth */}
      {!isAdminUser && (
        <>
          <TooltipWrapper title="Generate and display access link">
            <IconButton
              iconName="link"
              label="Link"
              labelAriaOnly={true}
              iconAlignment="left"
              intent="basic"
              kind="hollow"
              size="s"
              type="button"
              isLoading={false}
              width="responsive"
              height={{ type: "fixed" }}
              disabled={false}
              temporaryUnsafeSetSolidKindHack={false}
              onClick={() => onGenerateLink(data.id)}
              testId={`generate-link-${data.id}`}
            />
          </TooltipWrapper>
          <TooltipWrapper title="Send magic link via email">
            <IconButton
              iconName="mail"
              label="Email"
              labelAriaOnly={true}
              iconAlignment="left"
              intent="basic"
              kind="hollow"
              size="s"
              type="button"
              isLoading={false}
              width="responsive"
              height={{ type: "fixed" }}
              disabled={false}
              temporaryUnsafeSetSolidKindHack={false}
              onClick={() => onSendEmail(data.id)}
              testId={`send-email-${data.id}`}
            />
          </TooltipWrapper>
        </>
      )}
      <TooltipWrapper title="View login history">
        <IconButton
          iconName="list"
          label="History"
          labelAriaOnly={true}
          iconAlignment="left"
          intent="basic"
          kind="hollow"
          size="s"
          type="button"
          isLoading={false}
          width="responsive"
          height={{ type: "fixed" }}
          disabled={false}
          temporaryUnsafeSetSolidKindHack={false}
          onClick={() => onViewLoginHistory(data)}
          testId={`view-history-${data.id}`}
        />
      </TooltipWrapper>
      <TooltipWrapper title="Delete user">
        <IconButton
          iconName="trash"
          label="Delete"
          labelAriaOnly={true}
          iconAlignment="left"
          intent="danger"
          kind="hollow"
          size="s"
          type="button"
          isLoading={false}
          width="responsive"
          height={{ type: "fixed" }}
          disabled={false}
          temporaryUnsafeSetSolidKindHack={false}
          onClick={() => onDelete(data.id)}
          testId={`delete-user-${data.id}`}
        />
      </TooltipWrapper>
    </ActionsContainer>
  );
};

/**
 * AdminUserGrid Component
 */
const AdminUserGrid: React.FC<AdminUserGridProps> = ({
  rows,
  onEdit,
  onGenerateLink,
  onSendEmail,
  onDelete,
  onViewLoginHistory,
}) => {
  const columnDefs = useMemo<ColDef<UserRow>[]>(
    () => [
      {
        headerName: "ID",
        field: "id",
        width: 80,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Email",
        field: "email",
        flex: 1,
        minWidth: 200,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Name",
        field: "name",
        flex: 1,
        minWidth: 150,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Role",
        field: "role",
        cellRenderer: RoleCellRenderer,
        width: 150,
        filter: true,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Linked Customer",
        valueGetter: clientGetter,
        flex: 1,
        minWidth: 150,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Linked Supplier",
        valueGetter: supplierGetter,
        flex: 1,
        minWidth: 150,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Actions",
        sortable: false,
        filter: false,
        width: 220,
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: {
          onEdit,
          onGenerateLink,
          onSendEmail,
          onDelete,
          onViewLoginHistory,
        },
        suppressHeaderMenuButton: true,
      },
    ],
    [onEdit, onGenerateLink, onSendEmail, onDelete, onViewLoginHistory],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressHeaderMenuButton: true,
    }),
    [],
  );

  return (
    <GridWrapper data-testid="user-grid">
      <AgGridReact<UserRow>
        theme={gridTheme}
        rowData={rows as UserRow[]}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={(params) => params.data.id}
        suppressCellFocus={false}
      />
    </GridWrapper>
  );
};

export default AdminUserGrid;
